import { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import Link from "next/link";
import {
  background,
  text,
  border,
  borderRadius,
  shadow,
  heading,
  icon,
} from "@carvana/helios-styles";

import { AuctionLogo, Close, ExternalLink, Menu } from "@/components/icons";
import useScrollPosition from "@/hooks/useScrollPosition";
import {
  flexAlignHorizontalContent,
  flexAlignVerticalContent,
  LARGE_MOBILE,
  DESKTOP,
  mq,
} from "@/styles/mixins";
import BetaBadge from "./HeaderBetaBadge";
import TrackedLink from "../TrackedLink";

const Header = () => {
  const scrollPosition = useScrollPosition();
  const isSticky = scrollPosition > 400 - 70; // hero height - header height

  return (
    <HeaderContainer $isSticky={isSticky}>
      <MobileMenu />
      <Logo data-qa="header-logo">
        <Link href="/">
          <AuctionLogo />
        </Link>
        <BetaBadge />
      </Logo>
      <ButtonWrapper>
        <SignInButton
          href="/browse"
          segmentProps={{
            event: "ADESA Clear Sign In Clicked",
            props: {
              component: "Header Sign In Button",
            },
          }}
        >
          Sign In To ADESA Clear
        </SignInButton>

        <HelpButton
          href={process.env.NEXT_PUBLIC_CARVANA_AUCTION_HELP_URL}
          segmentProps={{
            event: "Help Clicked",
            props: {
              component: "Header Help Button",
            },
          }}
        >
          Help
          <ExternalLink />
        </HelpButton>
      </ButtonWrapper>
    </HeaderContainer>
  );
};

const MobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  // disable body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen]);

  return (
    <>
      <MenuButton onClick={toggleMenu}>
        <Menu />
      </MenuButton>
      {isOpen && (
        <MobileMenuContainer>
          <CloseButton onClick={toggleMenu}>
            <Close />
          </CloseButton>
          <MobileMenuTitle>Menu</MobileMenuTitle>
          <MenuItems>
            <MobileSignInButton
              href="/browse"
              segmentProps={{
                event: "ADESA Clear Sign In Clicked",
                props: {
                  component: "Header Sign In Button",
                },
              }}
            >
              Sign In To ADESA Clear
            </MobileSignInButton>
            <MobileHelpButton
              href={process.env.NEXT_PUBLIC_CARVANA_AUCTION_HELP_URL}
              segmentProps={{
                event: "Help Clicked",
                props: {
                  component: "Header Help Button",
                },
              }}
            >
              Help
            </MobileHelpButton>
          </MenuItems>
        </MobileMenuContainer>
      )}
    </>
  );
};

const HeaderContainer = styled.header<{ $isSticky: boolean }>`
  ${flexAlignHorizontalContent("space-between", "center")}
  gap: 18px;
  width: 100%;
  height: var(--header-height);
  background-color: ${background.default};
  top: 0;
  padding: 0 1.5rem;
  box-shadow: 0px 2px 3px rgba(24, 53, 88, 0.1);
  z-index: 300;

  > a,
  > div {
    flex-grow: 1;
    flex-basis: 0;
  }

  ${({ $isSticky }) =>
    $isSticky &&
    css`
      position: fixed;
      animation: slideDown 0.3s ease-in-out;

      @keyframes slideDown {
        from {
          transform: translateY(-100%);
        }
        to {
          transform: translateY(0);
        }
      }
    `}
`;

const Logo = styled.div`
  ${flexAlignHorizontalContent("center", "center")}
  gap: 5px;
`;

const ButtonWrapper = styled.div`
  ${flexAlignHorizontalContent("center", "center")}
  gap: 24px;

  ${mq(LARGE_MOBILE, "display: none;")}
`;

const HelpButton = styled(TrackedLink)`
  ${flexAlignHorizontalContent("center", "center")};
  gap: 9px;
  color: ${text.primary};
`;

const SignInButton = styled(TrackedLink)`
  text-decoration: none;
  cursor: pointer;
  color: ${text.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  position: absolute;
  min-height: fit-content;
  top: 16px;
  right: 16px;
  color: ${icon.strong};
  cursor: pointer;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: 0;

  ${mq(
    DESKTOP,
    `
    display: none;
  `,
  )}
`;

const MobileMenuContainer = styled.div`
  position: absolute;
  background: ${background.default};
  height: 100vh;
  inset: 0;
  padding: 16px;

  ${mq(
    DESKTOP,
    `
    display: none;
  `,
  )}
`;

const MobileMenuTitle = styled.div`
  ${heading.md}
  width: 100%;
  text-transform: uppercase;
  text-align: center;
`;

const MenuItems = styled.div`
  ${flexAlignVerticalContent("flex-start", "center")};
  gap: 16px;
  padding: 32px 0;
`;

const MobileSignInButton = styled(SignInButton)`
  min-height: 30px;
  ${flexAlignHorizontalContent("center", "center")};
  width: 100%;
  border-radius: ${borderRadius.round};
  padding: 12px 30px;
  background-color: ${background.primary};
  color: ${text.inverse};
  &:focus-visible {
    box-shadow:
      0px 0px 0px 3px rgba(34, 139, 230, 0.6),
      0px 0px 0px 1px ${shadow.focusVisible[1].color};
  }
  &:hover {
    color: ${text.inverse};
    background-color: ${background.primaryHover};
  }
  &:active {
    background-color: ${background.primaryActive};
  }
  &:disabled {
    background-color: ${background.disabled};
    color: ${text.disabled};
  }
`;

const MobileHelpButton = styled(HelpButton)`
  width: 100%;
  padding: 12px 30px;
  min-height: 30px;
  text-decoration: none;
  cursor: pointer;
  border-radius: ${borderRadius.round};
  padding: 12px 30px;
  box-shadow: inset 0px 0px 0px 1.5px ${border.strong};
  background-color: "transparent";
  color: ${text.strong};
  &:hover {
    box-shadow: none;
    background-color: ${background.primaryHover};
    color: ${text.inverse};
  }
  &:active {
    box-shadow: none;
    background-color: ${background.primaryActive};
    color: ${text.inverse};
  }
  &:disabled {
    box-shadow: inset 0px 0px 0px 1.5px ${border.disabled};
    color: ${text.disabled};
  }
  &:focus-visible {
    box-shadow:
      inset 0px 0px 0px 1.5px ${border.strong},
      0px 0px 0px 3px rgba(34, 139, 230, 0.6),
      0px 0px 0px 1px ${shadow.focusVisible[1].color};
  }
`;

export default Header;
